import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import CheckIcon from '@mui/icons-material/Check';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import type { DashboardProfile } from './types';

interface ProfileSwitcherProps {
  profiles: DashboardProfile[];
  activeProfile: DashboardProfile | null;
  isEditMode: boolean;
  onActivate: (profileId: number) => void;
  onCreateProfile: (name: string) => void;
  onDeleteProfile: (profileId: number) => void;
  onRenameProfile: (profileId: number, name: string) => void;
  onToggleEdit: () => void;
  onOpenWidgetPicker: () => void;
}

const ProfileSwitcher: React.FC<ProfileSwitcherProps> = ({
  profiles,
  activeProfile,
  isEditMode,
  onActivate,
  onCreateProfile,
  onDeleteProfile,
  onRenameProfile,
  onToggleEdit,
  onOpenWidgetPicker,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuProfileId, setMenuProfileId] = useState<number | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [dialogName, setDialogName] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, profileId: number) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuProfileId(profileId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuProfileId(null);
  };

  const handleCreateSubmit = () => {
    if (dialogName.trim()) {
      onCreateProfile(dialogName.trim());
      setDialogName('');
      setCreateDialogOpen(false);
    }
  };

  const handleRenameSubmit = () => {
    if (dialogName.trim() && menuProfileId) {
      onRenameProfile(menuProfileId, dialogName.trim());
      setDialogName('');
      setRenameDialogOpen(false);
      handleMenuClose();
    }
  };

  const handleDeleteConfirm = () => {
    if (menuProfileId) {
      onDeleteProfile(menuProfileId);
      setDeleteConfirmOpen(false);
      handleMenuClose();
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        {/* Profile Tabs */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flex: 1 }}>
          {profiles.map((profile) => (
            <Chip
              key={profile.id}
              label={profile.name}
              onClick={() => onActivate(profile.id)}
              onDelete={
                profiles.length > 1
                  ? (e) => {
                      e.stopPropagation();
                      handleMenuOpen(e as unknown as React.MouseEvent<HTMLElement>, profile.id);
                    }
                  : undefined
              }
              deleteIcon={<MoreVertIcon sx={{ fontSize: 16 }} />}
              variant={profile.is_active ? 'filled' : 'outlined'}
              sx={{
                fontWeight: profile.is_active ? 700 : 500,
                fontSize: '0.82rem',
                height: 32,
                ...(profile.is_active
                  ? {
                      bgcolor: alpha(theme.palette.primary.main, 0.15),
                      color: theme.palette.primary.main,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    }
                  : {
                      borderColor: isDark ? '#2e3560' : '#dfe3e8',
                      color: isDark ? '#8899aa' : '#5f7082',
                    }),
              }}
            />
          ))}
          <Tooltip title="Neues Profil">
            <IconButton
              size="small"
              onClick={() => {
                setDialogName('');
                setCreateDialogOpen(true);
              }}
              sx={{
                width: 32,
                height: 32,
                border: `1px dashed ${isDark ? '#2e3560' : '#dfe3e8'}`,
                color: isDark ? '#667788' : '#6b7c8d',
              }}
            >
              <AddIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Edit/Done Button */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isEditMode && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DashboardCustomizeIcon sx={{ fontSize: 16 }} />}
              onClick={onOpenWidgetPicker}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.78rem',
                height: 32,
              }}
            >
              Widget hinzufügen
            </Button>
          )}
          <Button
            variant={isEditMode ? 'contained' : 'outlined'}
            size="small"
            startIcon={isEditMode ? <CheckIcon sx={{ fontSize: 16 }} /> : <EditIcon sx={{ fontSize: 16 }} />}
            onClick={onToggleEdit}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.78rem',
              height: 32,
            }}
          >
            {isEditMode ? 'Fertig' : 'Bearbeiten'}
          </Button>
        </Box>
      </Box>

      {/* Profile Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            const profile = profiles.find((p) => p.id === menuProfileId);
            setDialogName(profile?.name || '');
            setRenameDialogOpen(true);
          }}
        >
          <DriveFileRenameOutlineIcon sx={{ fontSize: 18, mr: 1 }} />
          Umbenennen
        </MenuItem>
        {profiles.length > 1 && (
          <MenuItem
            onClick={() => setDeleteConfirmOpen(true)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon sx={{ fontSize: 18, mr: 1 }} />
            Löschen
          </MenuItem>
        )}
      </Menu>

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Neues Profil erstellen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Profilname"
            value={dialogName}
            onChange={(e) => setDialogName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateSubmit()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleCreateSubmit} disabled={!dialogName.trim()}>
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Profil umbenennen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Neuer Name"
            value={dialogName}
            onChange={(e) => setDialogName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleRenameSubmit()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleRenameSubmit} disabled={!dialogName.trim()}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
      >
        <DialogTitle>Profil löschen?</DialogTitle>
        <DialogContent>
          <Typography>
            Dieses Profil wird unwiderruflich gelöscht. Fortfahren?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProfileSwitcher;
